import { get } from 'lodash';

import { customerInTrial, isCustomerSubscriptionCanceled } from '../customer';

import { canSubscriptionBeReactivated, PLAN_PRICES, SEAT_PRICE_PER_MONTH } from './billing';
import {
  BILLING_CYCLES,
  SUBSCRIPTION_PLAN,
  SUBSCRIPTION_PLAN_ACTIVE,
  SUBSCRIPTION_SEAT_TIER,
  SUBSCRIPTION_TIER,
} from './const';
import {
  getPlanProperty,
  isPlanFromBasicCategory,
  isPlanFromBusinessCategory,
  isPlanFromDataDrivenCategory,
  isPlanFromEnterpriseCategory,
  isPlanFromStarterCategory,
  isSubscriptionPlanDeprecated,
} from './subscriptionPlan';
import {
  getPlanByTier,
  getSubscriptionTierSeatsLimit,
  getSubscriptionTierWordsLimit,
  getTierProperty,
} from './subscriptionTier';

export const getCustomerPlan = (customer) => get(customer, 'billingConfiguration.plan');

export const getCustomerTier = (customer) => get(customer, 'billingConfiguration.wordsTier');

export const getCustomerOldTier = (customer) => get(customer, 'planMigrationStatus.oldTier');

export const getCustomerOldPlan = (customer) => get(customer, 'planMigrationStatus.oldPlan');

export const getCustomerPendingTier = (customer) =>
  get(customer, 'billingConfiguration.planChangeRequest.wordsTier');

export const getCustomerPendingPlan = (customer) =>
  get(customer, 'billingConfiguration.planChangeRequest.plan');

export const getCustomerBillingCyclePeriod = (customer, suppressDefault) => {
  const billingCyclePeriod = customer?.billingConfiguration?.billingCyclePeriod;

  if (!billingCyclePeriod && !suppressDefault) {
    return BILLING_CYCLES.annually;
  }

  return billingCyclePeriod;
};

export const getCustomerBillingPeriodEnd = (customer) =>
  get(customer, 'billingConfiguration.currentPeriod.end');

export const getCustomerBillingMethod = (customer) => get(customer, 'billingConfiguration.method');

export const getCustomerBillingQueryQuota = (customer) =>
  get(customer, 'billingConfiguration.queryQuota');

export const getCustomerBillingDetails = (customer) =>
  get(customer, 'paymentMethod.billingDetails');

export const isSubscriptionPendingCancellation = (customer) => {
  const billingMethod = getCustomerBillingMethod(customer);
  const canBeReactivated = canSubscriptionBeReactivated(billingMethod);
  const cancelAtPeriodEnd = get(customer, 'billingConfiguration.cancelAtPeriodEnd');
  return canBeReactivated && cancelAtPeriodEnd;
};

export const getTierOrPlanProperty = (plan, tier, property, defaultValue = undefined) => {
  const tierValue = getTierProperty(tier, property, undefined, plan);
  if (tierValue) {
    return tierValue;
  }
  const planValue = getPlanProperty(plan, property);
  if (planValue) {
    return planValue;
  }
  return defaultValue;
};

export const getNumberOfMonthsDiscount = (plan, tier) =>
  getTierOrPlanProperty(plan, tier, 'numberOfMonthsDiscount', 0);

export const getPercentOfAnnualDiscount = (plan, tier) =>
  getTierOrPlanProperty(plan, tier, 'percentOfAnnualDiscount', 0);

export const getAnnualDiscountLabel = (plan, tier) => {
  const originalPrice = PLAN_PRICES[plan]?.annual;
  if (!originalPrice) {
    return '';
  }

  const seats = getTierOrPlanProperty(plan, tier, 'seats');
  const yearlyPricePerMonth = originalPrice + Math.max(0, seats - 3) * SEAT_PRICE_PER_MONTH.annual;

  const percent = Math.floor(
    100 * (1 - yearlyPricePerMonth / getTierOrPlanProperty(plan, tier, 'price'))
  );

  const numberOfMonthsDiscount = getNumberOfMonthsDiscount(plan, tier);
  return numberOfMonthsDiscount
    ? `${numberOfMonthsDiscount} months free`
    : percent
      ? `${percent}% off`
      : '';
};

export const isGreaterThanTier = (selectedTier, tier) =>
  getSubscriptionTierWordsLimit(selectedTier) > getSubscriptionTierWordsLimit(tier);

export const isLowerThanTier = (selectedTier, tier) =>
  getSubscriptionTierWordsLimit(selectedTier) < getSubscriptionTierWordsLimit(tier);

export const isDowngradingPlan = (currentPlan, plan) => {
  // If plans are the same, then it can't be treated as downgrading
  if (currentPlan === plan) {
    return false;
  }

  // "enterprise" are the highest plans type
  if (isPlanFromEnterpriseCategory(currentPlan)) {
    return true;
  }

  if (isPlanFromBusinessCategory(currentPlan)) {
    return (
      isPlanFromDataDrivenCategory(plan) ||
      isPlanFromStarterCategory(plan) ||
      isPlanFromBasicCategory(plan)
    );
  }

  if (isPlanFromDataDrivenCategory(currentPlan)) {
    return isPlanFromStarterCategory(plan) || isPlanFromBasicCategory(plan);
  }

  if (isPlanFromStarterCategory(currentPlan)) {
    return isPlanFromBasicCategory(plan);
  }

  return false;
};

export const isDowngradingSamePlanTier = ({ currentPlan, currentTier, plan, tier }) =>
  currentPlan === plan && isLowerSeatTier(currentPlan, currentTier, plan, tier);

const isLowerSeatTier = (currentPlan, currentTier, plan, tier) => {
  return (
    getSubscriptionTierSeatsLimit(currentTier, currentPlan) >
    getSubscriptionTierSeatsLimit(tier, plan)
  );
};

export const isMoreExpensiveTier = (currentTier, tier, plan) =>
  getTierProperty(tier, 'price', undefined, plan) >
  getTierProperty(currentTier, 'price', undefined, plan);

export const isDowngradingPlanOrTier = ({ currentPlan, currentTier, plan, tier }) => {
  if (isDowngradingPlan(currentPlan, plan) && isMoreExpensiveTier(currentTier, tier)) {
    return false;
  }

  return (
    isDowngradingPlan(currentPlan, plan) ||
    isDowngradingSamePlanTier({ currentPlan, currentTier, plan, tier })
  );
};

export const isPurchasingSubscription = (customer) => {
  const currentPlan = getCustomerPlan(customer);
  const isInTrial = customerInTrial(customer);
  const customerSubscriptionCanceled = isCustomerSubscriptionCanceled(customer);

  return isPlanFromBasicCategory(currentPlan) || isInTrial || customerSubscriptionCanceled;
};

export const actualizeSubscriptionPlan = (plan, defaultValue = SUBSCRIPTION_PLAN.DEFAULT) => {
  const clamped = SUBSCRIPTION_PLAN_ACTIVE.clamp(plan);
  if (!isSubscriptionPlanDeprecated(clamped)) {
    return clamped;
  }
  return defaultValue;
};

export const actualizeSubscriptionTierForPlan = (tier, plan) => {
  const clamped = SUBSCRIPTION_TIER.clamp(tier, null);
  const tierPlan = getPlanByTier(tier, null, plan);

  // Return early if tier is active and belongs to required plan
  if (clamped && tierPlan === plan) {
    return clamped;
  }

  // Tier fallbacks according to required plan
  switch (plan) {
    case SUBSCRIPTION_PLAN.dataDriven:
    case SUBSCRIPTION_PLAN.business:
      return SUBSCRIPTION_SEAT_TIER.seats_3;

    default:
      return null;
  }
};

export const isUpgradeFromMonthlyToAnnually = ({ currentBillingCycle, billingCycle }) =>
  currentBillingCycle === BILLING_CYCLES.monthly && billingCycle === BILLING_CYCLES.annually;
